import React, { useState, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    IconButton,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SaveIcon from '@mui/icons-material/Save';

// ── Data model ────────────────────────────────────────────────────────────────

export interface Preset {
    name: string;
    items: string[];
}

export interface PresetsConfig {
    /** Ordered list of presets. Max length is controlled by the component. */
    presets: Preset[];
}

// ── Persistence helpers ───────────────────────────────────────────────────────

function loadFromStorage(key: string, defaultCount: number): PresetsConfig {
    try {
        const raw = localStorage.getItem(key);
        if (raw) return JSON.parse(raw) as PresetsConfig;
    } catch { /* ignore malformed entry */ }
    return {
        presets: Array.from({ length: defaultCount }, (_, i) => ({
            name: `Preset ${i + 1}`,
            items: [],
        })),
    };
}

function saveToStorage(key: string, config: PresetsConfig): void {
    localStorage.setItem(key, JSON.stringify(config));
}

// ── Export / Import helpers ───────────────────────────────────────────────────

function exportToFile(storageKey: string, config: PresetsConfig): void {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${storageKey}-presets.json`;
    a.click();
    URL.revokeObjectURL(url);
}

function parseImportedConfig(json: string, maxPresets: number): PresetsConfig | null {
    try {
        const parsed = JSON.parse(json) as PresetsConfig;
        if (!Array.isArray(parsed?.presets)) return null;
        return { presets: parsed.presets.slice(0, maxPresets) };
    } catch {
        return null;
    }
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface PresetSelectorProps {
    /** Currently selected items — controlled from parent. */
    selectedItems: string[];
    /** Called when the user applies a preset or the selection otherwise changes. */
    onSelectionChange: (items: string[]) => void;
    /** localStorage key used for persistence and as the export file name prefix. */
    storageKey: string;
    /** Maximum number of presets the user can create. Default: 5. */
    maxPresets?: number;
    /** Number of empty presets to show on first use (before any localStorage entry). Default: 2. */
    defaultPresetCount?: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const PresetSelector: React.FC<PresetSelectorProps> = ({
    selectedItems,
    onSelectionChange,
    storageKey,
    maxPresets = 5,
    defaultPresetCount = 2,
}) => {
    const [config, setConfig] = useState<PresetsConfig>(() =>
        loadFromStorage(storageKey, defaultPresetCount)
    );

    // ── Inline rename state ───────────────────────────────────────────────────
    const [inlineEditIndex, setInlineEditIndex] = useState<number | null>(null);
    const [inlineEditName, setInlineEditName] = useState('');
    const [inlineEditWidth, setInlineEditWidth] = useState<number>(120);

    // ── Focused preset: the one targeted by Save / Remove ────────────────────
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

    // ── Drag-and-drop reorder state ─────────────────────────────
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const importInputRef = useRef<HTMLInputElement>(null);
    const isEditing = inlineEditIndex !== null;

    // Save is only meaningful when the current selection differs from what the preset already stores
    const focusedPresetItems = focusedIndex !== null ? (config.presets[focusedIndex]?.items ?? []) : null;
    const isDirty = focusedPresetItems !== null && (
        focusedPresetItems.length !== selectedItems.length ||
        selectedItems.some(x => !focusedPresetItems.includes(x))
    );

    // ── Persist on every change ───────────────────────────────────────────────
    const updateConfig = useCallback((next: PresetsConfig) => {
        setConfig(next);
        saveToStorage(storageKey, next);
    }, [storageKey]);

    // ── Apply: focus + apply selection (only if non-empty) ───────────────────
    const handleApply = useCallback((index: number) => {
        setFocusedIndex(index);
        const items = config.presets[index]?.items;
        if (items && items.length > 0) onSelectionChange(items);
    }, [config.presets, onSelectionChange]);

    // ── Add: new preset with current selection ────────────────────────────────
    const handleAdd = useCallback(() => {
        if (config.presets.length >= maxPresets) return;
        const name = `Preset ${config.presets.length + 1}`;
        const next = { presets: [...config.presets, { name, items: [...selectedItems] }] };
        updateConfig(next);
        setFocusedIndex(next.presets.length - 1);
    }, [config.presets, maxPresets, selectedItems, updateConfig]);

    // ── Save: write current selection into focused preset ────────────────────
    const handleSave = useCallback(() => {
        if (focusedIndex === null) return;
        updateConfig({
            presets: config.presets.map((p, i) =>
                i === focusedIndex ? { ...p, items: [...selectedItems] } : p
            ),
        });
    }, [focusedIndex, selectedItems, config.presets, updateConfig]);

    // ── Remove: delete focused preset ────────────────────────────────────────
    const handleRemove = useCallback(() => {
        if (focusedIndex === null) return;
        updateConfig({ presets: config.presets.filter((_, i) => i !== focusedIndex) });
        setFocusedIndex(null);
    }, [focusedIndex, config.presets, updateConfig]);

    // ── Inline rename ─────────────────────────────────────────────────────────
    const startInlineEdit = useCallback((index: number, width: number) => {
        setInlineEditIndex(index);
        setInlineEditName(config.presets[index].name);
        setFocusedIndex(index);
        setInlineEditWidth(width);
    }, [config.presets]);

    const commitRename = useCallback(() => {
        if (inlineEditIndex === null) return;
        const name = inlineEditName.trim() || config.presets[inlineEditIndex].name;
        updateConfig({
            presets: config.presets.map((p, i) => i === inlineEditIndex ? { ...p, name } : p),
        });
        setInlineEditIndex(null);
    }, [inlineEditIndex, inlineEditName, config.presets, updateConfig]);

    const cancelRename = useCallback(() => setInlineEditIndex(null), []);

    // ── Drag-and-drop handlers ────────────────────────────────────────────
    const handleDragStart = useCallback((index: number) => {
        setDragIndex(index);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (index !== dragIndex) setDragOverIndex(index);
    }, [dragIndex]);

    const handleDragEnd = useCallback(() => {
        setDragIndex(null);
        setDragOverIndex(null);
    }, []);

    const handleDrop = useCallback((targetIndex: number) => {
        if (dragIndex === null || dragIndex === targetIndex) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }
        const newPresets = [...config.presets];
        const [moved] = newPresets.splice(dragIndex, 1);
        newPresets.splice(targetIndex, 0, moved);
        updateConfig({ presets: newPresets });
        // Keep focusedIndex pointing at the same preset after reorder
        if (focusedIndex === dragIndex) {
            setFocusedIndex(targetIndex);
        } else if (focusedIndex !== null) {
            if (dragIndex < targetIndex && focusedIndex > dragIndex && focusedIndex <= targetIndex)
                setFocusedIndex(focusedIndex - 1);
            else if (dragIndex > targetIndex && focusedIndex >= targetIndex && focusedIndex < dragIndex)
                setFocusedIndex(focusedIndex + 1);
        }
        setDragIndex(null);
        setDragOverIndex(null);
    }, [dragIndex, config.presets, updateConfig, focusedIndex]);

    // ── Export / Import ───────────────────────────────────────────────────────
    const handleExport = useCallback(() => exportToFile(storageKey, config), [storageKey, config]);

    const handleImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const next = parseImportedConfig(ev.target?.result as string, maxPresets);
            if (next) { updateConfig(next); setFocusedIndex(null); }
        };
        reader.readAsText(file);
        e.target.value = '';
    }, [maxPresets, updateConfig]);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ overflowX: 'auto', flexShrink: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={0.5} flexWrap="nowrap" sx={{ width: 'max-content' }}>

            {/* ── Preset buttons ── */}
            {config.presets.map((preset, index) => (
                <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    gap={0}
                    draggable={!isEditing}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    onDrop={() => handleDrop(index)}
                    sx={{
                        opacity: dragIndex === index ? 0.35 : 1,
                        borderLeft: dragOverIndex === index && dragIndex !== index ? '3px solid' : '3px solid transparent',
                        borderColor: dragOverIndex === index && dragIndex !== index ? 'secondary.main' : 'transparent',
                        cursor: !isEditing ? 'grab' : undefined,
                        transition: 'opacity 0.15s, border-color 0.1s',
                    }}
                >
                    {inlineEditIndex === index ? (
                        /* Inline rename mode */
                        <>
                            <TextField
                                size="small"
                                value={inlineEditName}
                                onChange={e => setInlineEditName(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') commitRename();
                                    if (e.key === 'Escape') cancelRename();
                                }}
                                autoFocus
                                sx={{ width: inlineEditWidth, '& .MuiInputBase-input': { py: '3px', fontSize: '0.75rem' } }}
                                slotProps={{ htmlInput: { maxLength: 30 } }}
                            />
                            <Tooltip title="Confirm">
                                <IconButton size="small" onClick={commitRename} sx={{ p: '2px' }}>
                                    <CheckIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                                <IconButton size="small" onClick={cancelRename} sx={{ p: '2px' }}>
                                    <CloseIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        /* Normal mode */
                        <Tooltip
                            title={preset.items.length > 0
                                ? `Apply: ${preset.items.slice(0, 5).join(', ')}${preset.items.length > 5 ? ` +${preset.items.length - 5}` : ''}`
                                : 'Empty — click Save to store the current selection'}
                            placement="top"
                        >
                            <Button
                                size="small"
                                variant={focusedIndex === index ? 'contained' : 'outlined'}
                                color="secondary"
                                onClick={() => handleApply(index)}
                                sx={{
                                    fontSize: '0.72rem',
                                    py: 0.2,
                                    pl: 0.75,
                                    pr: 0.5,
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.4,
                                }}
                            >
                                {preset.name}
                                <Tooltip title={`Rename "${preset.name}"`} placement="top">
                                    <Box
                                        component="span"
                                        onClick={(e) => { e.stopPropagation(); const btn = (e.currentTarget as HTMLElement).closest('button'); startInlineEdit(index, btn?.offsetWidth ?? 120); }}
                                        sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}
                                    >
                                        <EditIcon sx={{ fontSize: 11 }} />
                                    </Box>
                                </Tooltip>
                            </Button>
                        </Tooltip>
                    )}
                </Stack>
            ))}

            {/* ── Add ── (hidden when at capacity) */}
            {config.presets.length < maxPresets && (
                <Tooltip title={selectedItems.length > 0 ? 'Add preset (saves current selection)' : 'Select items first'}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleAdd}
                            disabled={isEditing || selectedItems.length === 0}
                            sx={{ p: 0.25 }}
                        >
                            <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            )}

            {/* ── Save ── */}
            <Tooltip title={focusedIndex !== null
                ? `Save current selection to "${config.presets[focusedIndex]?.name}"`
                : 'Click a preset first'}>
                <span>
                    <IconButton
                        size="small"
                        onClick={handleSave}
                        disabled={isEditing || focusedIndex === null || selectedItems.length === 0 || !isDirty}
                        sx={{ p: 0.25 }}
                    >
                        <SaveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </span>
            </Tooltip>

            {/* ── Remove ── */}
            <Tooltip title={focusedIndex !== null
                ? `Remove "${config.presets[focusedIndex]?.name}"`
                : 'Click a preset first'}>
                <span>
                    <IconButton
                        size="small"
                        onClick={handleRemove}
                        disabled={isEditing || focusedIndex === null}
                        sx={{ p: 0.25 }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </span>
            </Tooltip>

            {/* ── Export / Import ── */}
            <Tooltip title="Export all presets to JSON file">
                <IconButton size="small" onClick={handleExport} disabled={isEditing} sx={{ p: 0.25 }}>
                    <FileDownloadIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            <Tooltip title="Import presets from JSON file (replaces current presets)">
                <IconButton size="small" onClick={() => importInputRef.current?.click()} disabled={isEditing} sx={{ p: 0.25 }}>
                    <FileUploadIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            <input
                ref={importInputRef}
                type="file"
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={handleImport}
            />
        </Stack>
        </Box>
    );
};

export default PresetSelector;
